// The design-hosted account chip (header button + account popup) drawn from the signed-in member's OWN verified avatar projection.
//
// The served design is a generated export that must not be rebuilt, and its header button is a static stock rider (`crew-3.webp`) whatever the session says.
// So the host paints over it with CSS only: two custom properties on <html> carry the member's picture / initials, and the rules below (attribute selectors on the
// design's own `button[data-sg-menu="user"]` and its signed-in popup avatar spans) read them. Being CSS, the paint survives every design re-render; there is no DOM
// patching and no observer. Signed out nothing is set and the rules do not apply, so the guest icon is exactly the design's.
//
// The crop is the shared one: height-fit at 120% and TOP aligned, so a face is never cut off at the forehead.

export const FACE_ATTR = "data-sg-own-face";
export const FACE_PICTURE_VAR = "--sg-own-face";
export const FACE_INITIALS_VAR = "--sg-own-initials";

/** Only an https address, or a same-origin path, may be painted; anything else is "no picture" (initials). */
export function paintableSrc(src: string | null | undefined): string | null {
  if (typeof src !== "string" || src.length === 0 || src.length > 2048) return null;
  return src.startsWith("https://") || (src.startsWith("/") && !src.startsWith("//")) ? src : null;
}

export interface DesignFace {
  /** "picture" when a paintable address is held, else "initials" */
  mode: "picture" | "initials";
  /** value of the picture custom property, or null */
  picture: string | null;
  /** value of the initials custom property: a quoted CSS string */
  initials: string;
}

export function designFace(face: { src: string | null; initials: string }): DesignFace {
  const src = paintableSrc(face.src);
  const letters = face.initials.replace(/[^\p{L}\p{N}•]/gu, "").slice(0, 2) || "•";
  return { mode: src ? "picture" : "initials", picture: src ? `url(${JSON.stringify(src)})` : null, initials: JSON.stringify(letters) };
}

const BTN = `html[${FACE_ATTR}] button[data-sg-menu="user"]`;
// The design's OWN-account avatar spans are marked `data-sg-own-avatar` by the host HTML-transform (design-sources.ts `markOwnAvatarTemplates`): the account popup, the mobile
// account menu, the sign-in sheet's signed-in view and the compact HUD Account button. Nothing else is ever painted (person, company and mountain `role="img"` spans carry no marker).
export const OWN_AVATAR_SELECTORS = [`html[${FACE_ATTR}="picture"] span[data-sg-own-avatar]`];

export const DESIGN_FACE_CSS = [
  `${BTN}{position:relative!important}`,
  `${BTN} img{opacity:0!important}`,
  `html[${FACE_ATTR}="picture"] button[data-sg-menu="user"]{background-image:var(${FACE_PICTURE_VAR})!important;background-repeat:no-repeat!important;background-position:50% 0%!important;background-size:auto 120%!important;background-color:#fff!important}`,
  `html[${FACE_ATTR}="initials"] button[data-sg-menu="user"]{background:#0f2f4c!important}`,
  `html[${FACE_ATTR}="initials"] button[data-sg-menu="user"]::after{content:var(${FACE_INITIALS_VAR});position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;font:600 14px/1 var(--sg-font-secondary,Arial,sans-serif)}`,
  // the popup / mobile-menu avatar spans: the design already draws the member's initials there; a saved picture replaces them with the same crop
  `${OWN_AVATAR_SELECTORS.join(",")}{background-image:var(${FACE_PICTURE_VAR})!important;background-repeat:no-repeat!important;background-position:50% 0%!important;background-size:auto 120%!important;background-color:#fff!important}`,
].join("\n");
